'use client';
import {
    ILocationDropDown,
    ISmallArea,
    useTranslation,
    useWindowDimensions,
} from '@/hook';
import { ICoordinate, IRegion_SELL } from '@/types';
import { useEffect, useState } from 'react';

import { MapWrapper } from '@/components/shared';
import Typography from '@/components/shared/typography/typography.component';
import {
    Disclaimer,
    StepperLocationDropdown,
} from '@/components/sell-car-form';
import { scrollToChild } from '@/utils';
import styles from './styles.module.scss';

interface LocationObject {
    bigArea: {
        en: string;
        ar: string;
    };
    smallArea: {
        id: string;
        nameEn: string;
        nameAr: string;
        selected: boolean;
        showAlert: boolean;
        polygon: ICoordinate[];
        serviceable: boolean;
        warehouse: boolean;
    };
    address: string;
    coordinate: {
        lat: number;
        lng: number;
    };
    addressCity: string;
    id: string;
}

const LocationFormContent = ({
    formContent,
    formFunction,
    step,
    open,
    searchPlaceholder,
    disclaimerText,
    intialValue,
    region,
}: {
    region: IRegion_SELL;
    formContent: ILocationDropDown[];
    step: 'region';
    formFunction: (key: 'region', clickedItem: IRegion_SELL) => void;
    open: boolean;
    searchPlaceholder: string;
    intialValue: ISmallArea;
    disclaimerText: string;
}) => {
    const [formContentState, setFormContentState] = useState(formContent);
    const [formFilteredContentState, setFormFilteredContentState] =
        useState(formContent);
    const [currArea, setCurrArea] = useState<LocationObject>();
    const { width } = useWindowDimensions();
    const [allowPinLocation, setAllowPinLocation] = useState<boolean>(
        region.sendPinLocation
    );
    const setPinLocation = (allowSend: boolean) => {
        setAllowPinLocation(allowSend);
    };
    const [currRegion, setCurrRegion] = useState<IRegion_SELL>(region);
    //@TODO :: CHANGE THIS>
    const IS_DESKTOP = width >= 1100;
    const { t } = useTranslation();
    const getCenter = (polygon: ICoordinate[]): ICoordinate => {
        const summed = polygon.reduce(
            (prev, curr) => {
                return { lat: prev.lat + curr.lat, lng: prev.lng + curr.lng };
            },
            { lat: 0, lng: 0 }
        );
        return {
            lat: summed.lat / polygon.length,
            lng: summed.lng / polygon.length,
        };
    };
    const setIntialData = () => {
        let currSmallArea: ISmallArea = {
            id: '',
            nameEn: '',
            nameAr: '',
            selected: false,
            showAlert: false,
            polygon: [] as ICoordinate[],
            serviceable: false,
            warehouse: false,
        };
        const filteredArr = formContent?.map(region => {
            const smallArea = region.smallArea.map(smallRegion => {
                if (
                    intialValue.nameEn === smallRegion.nameEn ||
                    intialValue.nameAr === smallRegion.nameAr
                )
                    currSmallArea = smallRegion;
                return {
                    ...smallRegion,
                    selected:
                        intialValue.nameEn === smallRegion.nameEn ||
                        intialValue.nameAr === smallRegion.nameAr,
                };
            });

            return { ...region, smallArea, expanded: true };
        });
        setFormContentState(filteredArr);
        setFormFilteredContentState(filteredArr);

        const tempLocationObject: LocationObject = {
            bigArea: { en: region.bigArea, ar: region.bigAreaAr },
            smallArea: currSmallArea,
            address: region.address,
            coordinate: region.coordinate || getCenter(region.polygon),
            addressCity: region.addressCity,
            id: region.id,
        };
        setCurrArea(tempLocationObject);
    };
    useEffect(() => {
        setIntialData();
    }, [formContent]);

    const sendLocationObj = (
        bigArea: { en: string; ar: string },
        smallArea: ISmallArea,
        id: string,
        coordinate: ICoordinate | null,
        address: string,
        addressCity: string,
        sendPinLocation: boolean
    ) => {
        const obj: IRegion_SELL = {
            id: id,
            bigArea: bigArea.en,
            smallArea: smallArea.nameEn,
            name: smallArea.nameEn,
            bigAreaAr: bigArea.ar,
            smallAreaAr: smallArea?.nameAr,
            address: address || '',
            coordinate: coordinate || null,
            polygon: smallArea.polygon,
            serviceable: smallArea.serviceable,
            warehouse: smallArea.warehouse,
            addressCity: addressCity,
            sendPinLocation: sendPinLocation,
        };
        setCurrRegion(obj);
        if (IS_DESKTOP) formFunction(step, obj);
    };
    const buttonClickedMap = (
        id: string,
        value: string,
        address: string,
        city: string,
        coordinate: ICoordinate | null
    ) => {
        setPinLocation(true);
        buttonClicked(id, value, address, city, coordinate, true);
    };
    const buttonCLickedArea = (id: string, value: string) => {
        setPinLocation(false);
        buttonClicked(id, value, '', '', null, false);
    };
    const buttonClicked = (
        id: string,
        value: string,
        address: string,
        city: string,
        coordinate: ICoordinate | null,
        allowPin: boolean
    ) => {
        let tempLocationObject: LocationObject = {
            bigArea: { en: '', ar: '' },
            smallArea: {
                id: '',
                nameEn: '',
                nameAr: '',
                selected: false,
                showAlert: false,
                polygon: [] as ICoordinate[],
                serviceable: false,
                warehouse: false,
            },
            address: address,
            coordinate: coordinate || { lat: 0, lng: 0 },
            addressCity: city,
            id: '',
        };
        const newState = formContentState.map(location => {
            const smallAreas = location.smallArea.map(smallArea => {
                const area: ISmallArea = {
                    ...smallArea,
                    selected: value === smallArea?.nameEn,
                };
                if (value === smallArea?.nameEn)
                    tempLocationObject = {
                        bigArea: {
                            en: location.bigAreaEn,
                            ar: location.bigAreaAr,
                        },
                        smallArea,
                        address: address,
                        coordinate: coordinate || getCenter(smallArea.polygon),
                        addressCity: city,
                        id: location.id,
                    };
                return area;
            });
            return {
                ...location,
                expanded: false,
                smallArea: [...smallAreas],
            };
        });
        const newFiltredState = formFilteredContentState.map(location => {
            let expanded = false;
            const smallAreas = location.smallArea.map(smallArea => {
                const area: ISmallArea = {
                    ...smallArea,
                    selected: value === smallArea?.nameEn,
                };
                if (value === smallArea?.nameEn) {
                    tempLocationObject = {
                        bigArea: {
                            en: location.bigAreaEn,
                            ar: location.bigAreaAr,
                        },
                        smallArea,
                        address: address,
                        coordinate: coordinate || getCenter(smallArea.polygon),
                        addressCity: city,
                        id: location.id,
                    };
                    setCurrArea(tempLocationObject);
                    expanded = true;
                }
                return area;
            });
            if (expanded && address) {
                const selected = tempLocationObject.smallArea;
                if (selected) {
                    const element = document.getElementById(
                        `radioButton_${selected.id || ''}`
                    );
                    const parent = document.getElementById(
                        `location_${location.id}`
                    );
                    const container = document.getElementById(
                        `location_outer_${location.id}`
                    );
                    if (element && parent && container && !IS_DESKTOP) {
                        scrollToChild(parent, element);
                        const y =
                            container.getBoundingClientRect().top +
                            window.scrollY;
                        container.scrollIntoView(true);
                        window.scroll({
                            top: y,
                            behavior: 'smooth',
                        });
                    }
                }
            }
            return {
                ...location,
                expanded: expanded,
                smallArea: [...smallAreas],
            };
        });

        sendLocationObj(
            {
                en: tempLocationObject.bigArea.en
                    ? tempLocationObject.bigArea.en
                    : city,
                ar: tempLocationObject.bigArea.ar
                    ? tempLocationObject.bigArea.ar
                    : city,
            },
            tempLocationObject.smallArea,
            id,
            coordinate || getCenter(tempLocationObject.smallArea.polygon),
            address,
            city,
            allowPin
        );
        setFormContentState(newState);
        setFormFilteredContentState(newFiltredState);
    };
    const handleConfirm = () => {
        if (currArea) {
            if (currArea.smallArea?.nameEn) {
                sendLocationObj(
                    { en: currArea.bigArea.en, ar: currArea.bigArea.ar },
                    currArea.smallArea,
                    currArea?.id,
                    currArea.coordinate || { lat: 30.0444, lng: 31.2357 },
                    currArea.address,
                    currArea.addressCity,
                    allowPinLocation
                );
            } else {
                sendLocationObj(
                    { en: currArea.addressCity, ar: currArea.addressCity },
                    currArea.smallArea,
                    currArea?.addressCity,
                    currArea.coordinate || { lat: 30.0444, lng: 31.2357 },
                    currArea.address,
                    currArea.addressCity,
                    allowPinLocation
                );
            }
            formFunction(step, currRegion);
        }
    };
    return (
        <div
            className={`scrollable ${styles.container}`}
            id={'container_location'}
        >
            {disclaimerText !== '' && (
                <Disclaimer
                    variant={
                        currArea && currArea.smallArea?.warehouse
                            ? 'error'
                            : 'warning'
                    }
                    text={disclaimerText}
                />
            )}
            <MapWrapper
                region={currRegion}
                regions={formContentState}
                buttonClicked={buttonClickedMap}
            />
            <Typography variant="body2Medium">{t('selectYourArea')}</Typography>
            <section
                className={`${styles.options_container} ${
                    open ? styles.open : ''
                }`}
                id={'options'}
            >
                {formFilteredContentState?.map(location => (
                    <StepperLocationDropdown
                        key={location?.bigAreaEn}
                        location={location}
                        buttonClicked={buttonCLickedArea}
                    />
                ))}
            </section>

            <div className={styles.button_container}>
                <button
                    className="sylndr_button primary"
                    style={{ height: '48px' }}
                    onClick={() => handleConfirm()}
                    disabled={!(currRegion.address || currRegion.smallArea)}
                >
                    <Typography>{t('continue')}</Typography>
                </button>
            </div>
        </div>
    );
};
export default LocationFormContent;
